import {
  loadable,
  Memoed,
  memoed,
  SimpleUnit,
  useOrganizationPermissions,
  useUnits,
} from '@library/react-toolkit'
import { TablePaginationConfig } from 'antd'
import { SorterResult } from 'antd/lib/table/interface'
import { array, option } from 'fp-ts'
import { pipe } from 'fp-ts/lib/function'
import { DefaultRootState, useDispatch, useSelector } from 'react-redux'
import { sortFromSorterResult } from './types'

const formatRegex = /-1(0*) ([^ ]+)/

export const unitToUnitInfo = (u: SimpleUnit) => {
  const matchedFormat = pipe(
    array.head(u.format),
    option.map(f => formatRegex.exec(f)),
    option.chain(option.fromNullable)
  )
  const symbol = pipe(
    matchedFormat,
    option.chain(array.lookup(2)),
    option.getOrElse(() => u.name)
  )
  const zeros = pipe(matchedFormat, option.chain(array.lookup(1)))
  const fractionDigits = pipe(
    zeros,
    option.map(x => x.length),
    option.getOrElse(() => 0)
  )
  const divider = pipe(
    zeros,
    option.map(x => 10 ** x.length),
    option.getOrElse(() => 0)
  )
  return { name: u.name, symbol, fractionDigits, divider }
}

export const useUnitInfo = (unitID: string) => {
  const units = useUnits()

  if (units[unitID]) {
    return unitToUnitInfo(units[unitID])
  }

  return {
    name: '',
    symbol: 'TND',
    fractionDigits: 3,
    divider: 1000,
  }
}

export const usePBLPermission = () => {
  const permissions = useOrganizationPermissions()
  return permissions?.modulePermissions?.PROSPERBILL ?? 'NONE'
}

type PaginatedData<T> = {
  data: T[]
  count: number
}

type WithLoading<T> = T & { loading: boolean }

export const usePaginatedData = <Item extends object>(
  selector: (_: DefaultRootState) => Memoed<PaginatedData<Item>>,
  keyGen: keyof Item | ((_: Item) => string)
): WithLoading<PaginatedData<Item & { key: string }>> => {
  const memoedData = useSelector(selector)
  const { data, count } = memoed.extract(memoedData)
  const getKey =
    typeof keyGen === 'function' ? keyGen : (i: Item) => `${i[keyGen]}`

  return {
    count,
    data: data.map(i => ({ ...i, key: getKey(i) })),
    loading: loadable.isLoading(memoedData.v),
  }
}

type Query<T> = {
  page: number
  itemsPerPage: number
  sort?: [keyof T, 'asc' | 'desc']
}

export const useOnChangeHandler = <Item extends Record<string, any>>(
  a: (_: Partial<Query<Item>>) => any
) => {
  const dispatch = useDispatch()
  return (
    { current, pageSize }: TablePaginationConfig,
    _: unknown,
    sorter:
      | SorterResult<Item & { key: string }>
      | SorterResult<Item & { key: string }>[]
  ) => {
    const action = a({
      ...(!!current ? { page: current - 1 } : {}),
      ...(!!pageSize ? { itemsPerPage: pageSize } : {}),
      sort: sortFromSorterResult<
        keyof Item extends string ? keyof Item : never,
        Item & { key: string }
      >(sorter),
    })
    if (
      action !== 'undefind' &&
      typeof action === 'object' &&
      'type' in action
    ) {
      dispatch(a)
    }
  }
}
